import type React from "react";

/**
 * StatusPill — pill leve com label de status. Padrão único pro app inteiro.
 *
 * Antes: cada página (Quadro, Painel, Resultado, Execucao) tinha seu próprio
 * mapping de status -> label/cor. Agora vem daqui.
 */

export type IssueStatus =
  | "backlog"
  | "todo"
  | "in_progress"
  | "in_review"
  | "blocked"
  | "done"
  | "cancelled";

export type RunStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "completed"
  | "failed"
  | "cancelled";

const ISSUE_STATUS_META: Record<IssueStatus, { label: string; bg: string; fg: string }> = {
  backlog: { label: "no backlog", bg: "rgba(0,0,0,0.04)", fg: "var(--wf-ink-2)" },
  todo: { label: "em criação", bg: "rgba(0,0,0,0.04)", fg: "var(--wf-ink-2)" },
  in_progress: { label: "executando", bg: "rgba(255,112,0,0.10)", fg: "var(--nd-orange-pun)" },
  in_review: { label: "para revisar", bg: "rgba(255,112,0,0.10)", fg: "var(--nd-orange-pun)" },
  blocked: { label: "bloqueado", bg: "rgba(204,51,68,0.10)", fg: "#cc3344" },
  done: { label: "concluído", bg: "rgba(92,184,92,0.12)", fg: "#3d7d3d" },
  cancelled: { label: "cancelado", bg: "rgba(0,0,0,0.04)", fg: "var(--wf-ink-3)" },
};

const RUN_STATUS_META: Record<RunStatus, { label: string; bg: string; fg: string }> = {
  queued: { label: "na fila", bg: "rgba(0,0,0,0.04)", fg: "var(--wf-ink-2)" },
  running: { label: "executando", bg: "rgba(255,112,0,0.10)", fg: "var(--nd-orange-pun)" },
  succeeded: { label: "concluído", bg: "rgba(92,184,92,0.12)", fg: "#3d7d3d" },
  completed: { label: "concluído", bg: "rgba(92,184,92,0.12)", fg: "#3d7d3d" },
  failed: { label: "falhou", bg: "rgba(204,51,68,0.10)", fg: "#cc3344" },
  cancelled: { label: "cancelado", bg: "rgba(0,0,0,0.04)", fg: "var(--wf-ink-3)" },
};

export function StatusPill({
  status,
  size = "md",
  style,
}: {
  status: string;
  size?: "sm" | "md";
  style?: React.CSSProperties;
}) {
  const meta =
    ISSUE_STATUS_META[status as IssueStatus] ??
    RUN_STATUS_META[status as RunStatus] ?? {
      label: status,
      bg: "rgba(0,0,0,0.04)",
      fg: "var(--wf-ink-2)",
    };
  const isSm = size === "sm";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: isSm ? "1px 8px" : "2px 10px",
        borderRadius: 999,
        background: meta.bg,
        color: meta.fg,
        fontSize: isSm ? 10 : 11,
        fontWeight: 600,
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
        fontFamily: "var(--wf-font-body)",
        ...style,
      }}
    >
      {meta.label}
    </span>
  );
}
